import { useDates, useProjectOf, useProjects, useTasks, useAllEdges } from '@/db/hooks'
import { withSteps } from '@/lib/steps'
import { useListNav } from '@/hooks/useKeyboard'
import { isOpen, isTemplate } from '@/kinds/task'
import { addDays, fmtDateShort, todayLocal } from '@/lib/time'
import { compareToday } from '@/lib/today'
import { TaskRow } from '@/components/TaskRow'
import { DateRow } from '@/components/DateBits'
import { occurrencesBetween } from '@/lib/dates'
import type { DateResource } from '@/kinds/date/apply'
import { Empty, PageTitle } from '@/components/ui'
import { AppTabs, AHEAD_TABS } from '@/components/AppTabs'

/** Next 14 days by scheduled date (with the dates on each day first), then unscheduled tasks that have a due date. */
export const UpcomingView = () => {
  const today = todayLocal()
  const end = addDays(today, 14)
  const tasks = (useTasks() ?? []).filter((t) => isOpen(t.status.state) && !isTemplate(t.spec))
  const projects = useProjects() ?? []
  const byId = new Map(projects.map((p) => [p.id, p]))
  const projectOf = useProjectOf()
  const scheduled = tasks
    .filter((t) => t.spec.scheduled && t.spec.scheduled <= end)
    .sort((a, b) =>
      a.spec.scheduled! < b.spec.scheduled!
        ? -1
        : a.spec.scheduled! > b.spec.scheduled!
          ? 1
          : compareToday(a, b),
    )
  const groups = new Map<string, typeof scheduled>()
  for (const t of scheduled) {
    const key = t.spec.scheduled! < today ? today : t.spec.scheduled!
    groups.set(key, [...(groups.get(key) ?? []), t])
  }
  // dates ride in the same days, ahead of the tasks: a birthday is the day's context
  const datesOn = new Map<string, DateResource[]>()
  for (const d of useDates())
    for (const on of occurrencesBetween(d.spec, today, end)) datesOn.set(on, [...(datesOn.get(on) ?? []), d])
  const days = [...new Set([...groups.keys(), ...datesOn.keys()])].sort()
  const dueOnly = tasks
    .filter((t) => !t.spec.scheduled && t.spec.due)
    .sort((a, b) => (a.spec.due! < b.spec.due! ? -1 : 1))
  // Next or Waiting with no date at all: not on Today (needs a date), not in the
  // Inbox or Someday (other states), so without this they are nowhere at all
  const undated = tasks
    .filter(
      (t) => !t.spec.scheduled && !t.spec.due && (t.status.state === 'next' || t.status.state === 'waiting'),
    )
    .sort(compareToday)
  const ordered = [...scheduled, ...dueOnly, ...undated]
  const cursor = useListNav(ordered.map((t) => t.id))
  const indexOf = new Map(ordered.map((t, i) => [t.id, i]))
  const edges = useAllEdges()
  const row = ({ task: t, child }: { task: (typeof ordered)[number]; child: boolean }) => (
    <TaskRow
      key={t.id}
      task={t}
      index={indexOf.get(t.id)}
      focused={cursor === indexOf.get(t.id)}
      project={byId.get(projectOf.get(t.id) ?? '')}
      child={child}
    />
  )
  // steps sit under their parent within a day's group
  const rows = (items: (typeof ordered)[number][]) => withSteps(items, edges).map(row)
  return (
    <div>
      <PageTitle>Calendar</PageTitle>
      <AppTabs tabs={AHEAD_TABS} label="Calendar" />
      {ordered.length === 0 && days.length === 0 && <Empty>Nothing on the horizon.</Empty>}
      {days.map((day) => (
        <section key={day} className="mb-4">
          <h2 className="text-[13px] font-semibold text-muted mb-2">{fmtDateShort(day, today)}</h2>
          <ul className="group-list">
            {(datesOn.get(day) ?? []).map((d) => (
              <DateRow key={d.id} date={d} on={day} today={today} />
            ))}
            {rows(groups.get(day) ?? [])}
          </ul>
        </section>
      ))}
      {dueOnly.length > 0 && (
        <section className="mb-4">
          <h2 className="text-[13px] font-semibold text-muted mb-2">Has a deadline, not yet scheduled</h2>
          <ul className="group-list">{rows(dueOnly)}</ul>
        </section>
      )}
      {undated.length > 0 && (
        <section className="mb-4" id="nodate" data-testid="upcoming-undated">
          <h2 className="text-[13px] font-semibold text-muted mb-2">No date yet</h2>
          <p className="text-xs text-faint mb-2">
            Ready to do, but not placed on a day. Give one a day and it joins Today.
          </p>
          <ul className="group-list">{rows(undated)}</ul>
        </section>
      )}
    </div>
  )
}
